// @ts-strict-ignore
import * as React from 'react';
import {PureComponent} from 'react';
import {Location, useLocation} from 'react-router';
import isEmpty from 'lodash/isEmpty';

import {Dashboard, DashboardCard, DisplayBookmark} from 'common/report/dashboards/types';
import {
  getActiveBoardFilters,
  getBoardFiltersFromPersisted,
  isBenchmarkCard,
} from 'apps/boards/src/dashboards-app/util';
import {isBrowseUrlData, parseDashboardUrlHashParams, STAGED_CARD_PREFIX} from 'common/report/dashboards/url';
import {DashboardsContext} from 'apps/boards/src/util/context';
import {getChartOptions} from 'apps/boards/src/report-util';
import {DashboardSelectorController} from 'common/report/controllers/dashboard-selector-controller';
import {reporter} from 'common/report/error-reporting';
import {DashboardBookmarkOverrides} from 'common/report/util/bookmark-overrides';
import {ReportEditorReact} from 'common/report-editor';
import {IronContextRegistry} from 'common/report/context-registry';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {useMetrics} from 'common/contexts/react/metrics-resource-context';
import {useBehaviors} from 'common/contexts/react/behaviors-context';
import {useDashboardSelectorController} from 'common/contexts/react/dashboard-selector-controller-context';
import {useTranslation} from 'common/i18n/hooks';

type State = {
  editorCard: DashboardCard;
  editorCardEditedBookmark: DisplayBookmark;
  editorCardBookmarkURLSlug: string;
  overrides: DashboardBookmarkOverrides;
  dashboard: Dashboard;
  dashboardFailed: boolean;
  dashboardLoaded: boolean;
};

type Props = {
  context: DashboardsContext;
  elref: string;
};

class DownloadModeClass extends PureComponent<
  Props &
    Pick<IronContextRegistry, `currentProject` | `metrics` | `behaviors` | `i18n`> & {
      location: Location;
      selectorController: DashboardSelectorController;
    },
  State
> {
  public declare loadPromise: Promise<void>;

  state: State = {
    editorCard: null,
    editorCardEditedBookmark: null,
    editorCardBookmarkURLSlug: null,
    dashboard: null,
    dashboardFailed: false,
    dashboardLoaded: false,
    overrides: null,
  };

  componentDidMount() {
    if (!this.props.context) {
      throw new Error(`"context" is a required component prop`);
    }
    if (!this.props.selectorController) {
      throw new Error(`"selectorController" is a required component prop`);
    }
    this.props.selectorController.resetActiveDashboard();

    this.load();
  }

  private async load() {
    try {
      this.setState({dashboardLoaded: false});

      const {parsedData} = parseDashboardUrlHashParams(this.props.location.hash.slice(1));
      if (isBrowseUrlData(parsedData) || (!parsedData.editorCardId && !parsedData.editorCardBookmarkURLSlug)) {
        throw new Error(`invalid download URL: card ID is not present`);
      }

      // get selected dashboard's model controller if not already selected
      let dashboardController = this.props.selectorController.getActiveDashboardController();
      if (!dashboardController || dashboardController.getModel().id !== parsedData.dashboardId) {
        await this.props.selectorController.changeActiveDashboard(parsedData.dashboardId);
        dashboardController = this.props.selectorController.getActiveDashboardController();
      }

      // load dashboard's model controller if not already loaded
      if (!dashboardController.getLoaded()) {
        await dashboardController.loadModel();
      }

      const dashboard = dashboardController.getModel();
      if (dashboardController.getError()) {
        throw new Error(`error loading board`);
      }

      const editorCard = dashboard.cards.find(({id}) => id === parsedData.editorCardId);
      const isStagedCard =
        parsedData.editorCardId?.toString()?.startsWith(STAGED_CARD_PREFIX) && !!parsedData.editorCardEditedBookmark;

      if (!editorCard && !isStagedCard && !parsedData.editorCardBookmarkURLSlug) {
        throw new Error(`card ID '${parsedData.editorCardId}' does not exist on board`);
      }

      const timeFilter = parsedData.timeFilter ?? dashboard.time_filter;
      const additionalFilters = getBoardFiltersFromPersisted(parsedData.filters ?? dashboard.filters);

      let overrides: DashboardBookmarkOverrides = null;
      // only need to apply overrides to saved cards
      if (
        editorCard &&
        `bookmark` in editorCard &&
        !isBenchmarkCard(editorCard) && // Benchmark cards have bookmark but does not have edit mode
        (`chart_options` in editorCard || !isEmpty(timeFilter) || additionalFilters?.length)
      ) {
        const chartTypeOverride = getChartOptions({
          bookmark: editorCard.bookmark,
          chartOptionsOverride: editorCard.chart_options,
          metricsResource: this.props.metrics,
          behaviorsResource: this.props.behaviors,
          currentProject: this.props.currentProject.get().value,
        }).type;

        overrides = {
          dateRangeOverride: timeFilter?.dateRange,
          additionalFilters: getActiveBoardFilters(additionalFilters),
          additionalBreakdowns: parsedData.breakdowns ?? dashboard.breakdowns,
          chartTypeOverride,
        };
      }

      this.setState({
        editorCard,
        editorCardEditedBookmark: parsedData.editorCardEditedBookmark,
        editorCardBookmarkURLSlug: parsedData.editorCardBookmarkURLSlug,
        dashboard,
        dashboardLoaded: true,
        overrides,
      });
    } catch (err) {
      this.setState({dashboardFailed: true});
      reporter.error(err);
    }
  }

  render() {
    return (
      <div elref={this.props.elref}>
        <ReportEditorReact
          editorParams={{
            originalBookmark: this.state.editorCard?.bookmark,
            editedBookmark: this.state.editorCardEditedBookmark,
            bookmarkURLSlug: this.state.editorCardBookmarkURLSlug,
            dashboardOverrides: this.state.overrides,
            dashboardCardId: this.state.editorCard?.id,
            resultsPromise: null,
          }}
          loading={!this.state.dashboardLoaded}
          elref={`reportEditor`}
        />
      </div>
    );
  }
}

export type {DownloadModeClass};

export const DownloadMode = (props: Props) => {
  const behaviors = useBehaviors();
  const currentProject = useCurrentProject();
  const location = useLocation();
  const metrics = useMetrics();
  const selectorController = useDashboardSelectorController();
  const i18n = useTranslation();

  return (
    <DownloadModeClass
      behaviors={behaviors}
      currentProject={currentProject}
      location={location}
      metrics={metrics}
      selectorController={selectorController}
      i18n={i18n}
      {...props}
    />
  );
};
